/**
 * StockLocationsPage - Endereços de estoque (Depósito, Loja, Escritório, Casa do Cliente)
 *
 * RELEASE: 2026-10-09 v1.1.0 - Filtros rápidos no estilo pill padrão (QUICK_FILTER_PILL) e busca com lupa.
 * RELEASE: 2026-10-06 v1.0.0 - Versão inicial (Fase 2 do Estoque).
 */
import { useEffect, useState } from 'react';
import { MapPin, Plus, Power, Pencil, MoreHorizontal, Search } from 'lucide-react';
import { QUICK_FILTER_PILL } from '@/components/layout/list-page/quickFilterStyles';
import {
  ListPageLayout, ListPageHeader, ListPageFiltersBar, ListPageContent,
  LIST_FILTERS_INPUT_CLASS, LIST_FILTERS_SELECT_TRIGGER_CLASS,
} from '@/components/layout/list-page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import { usePermissionsContext } from '@/contexts/PermissionsContext';
import {
  STOCK_LOCATION_TYPES, stockLocationTypeLabel, useStockLocations, useStockRegistryMutations,
  type StockListFilters, type StockLocation,
} from '@/hooks/useStockRegistries';
import { StockLocationFormDialog } from '@/components/stock/StockLocationFormDialog';

export default function StockLocationsPage() {
  const { canCreate, canEdit, isAdmin } = usePermissionsContext();
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState<StockListFilters>({ search: '', status: 'active', type: 'all', page: 1, pageSize: 25 });
  const [editor, setEditor] = useState<{ open: boolean; item: StockLocation | null }>({ open: false, item: null });
  const { data, isLoading } = useStockLocations(filters);
  const { upsertLocation } = useStockRegistryMutations();

  // Busca com atraso curto (uma consulta por pausa de digitação)
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.search === searchInput.trim() ? f : { ...f, search: searchInput.trim(), page: 1 })), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const allowCreate = isAdmin || canCreate('stock_locations');
  const allowEdit = isAdmin || canEdit('stock_locations');
  const items = data?.items ?? [];

  /** Ativa/inativa reenviando o cadastro (mesma RPC de edição). */
  const toggleActive = (l: StockLocation) => upsertLocation.mutate({ id: l.id, data: { ...l, is_active: !l.is_active } });

  return (
    <ListPageLayout>
      <ListPageHeader
        title="Endereços de estoque"
        actions={allowCreate ? (
          <Button size="xs" onClick={() => setEditor({ open: true, item: null })}>
            <Plus className="mr-1.5 h-4 w-4" /> Novo endereço
          </Button>
        ) : undefined}
      />
      <ListPageFiltersBar>
        {/* Busca com lupa, mesmo padrão da listagem de Propostas */}
        <div className="relative min-w-0 flex-1 sm:max-w-sm">
          <Search className="absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground sm:h-3.5 sm:w-3.5" />
          <Input className={`${LIST_FILTERS_INPUT_CLASS} w-full pl-8`} value={searchInput} maxLength={100}
          placeholder="Buscar por nome ou cidade..." onChange={(e) => setSearchInput(e.target.value)} />
        </div>
        <Select value={filters.type} onValueChange={(v) => setFilters((f) => ({ ...f, type: v as StockListFilters['type'], page: 1 }))}>
          <SelectTrigger className={`${LIST_FILTERS_SELECT_TRIGGER_CLASS} ${QUICK_FILTER_PILL} shrink-0`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os tipos</SelectItem>
            {STOCK_LOCATION_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v as StockListFilters['status'], page: 1 }))}>
          <SelectTrigger className={`${LIST_FILTERS_SELECT_TRIGGER_CLASS} ${QUICK_FILTER_PILL} shrink-0`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Ativos</SelectItem>
            <SelectItem value="inactive">Inativos</SelectItem>
            <SelectItem value="all">Todos</SelectItem>
          </SelectContent>
        </Select>
      </ListPageFiltersBar>
      <ListPageContent>
        {isLoading ? <Skeleton className="h-40 w-full rounded-lg" /> : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <MapPin className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Nenhum endereço encontrado.</p>
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <Table className="min-w-[720px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead className="w-36">Tipo</TableHead>
                  <TableHead>Endereço</TableHead>
                  <TableHead className="w-24">Situação</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((l) => (
                  <TableRow key={l.id} className="cursor-pointer" onClick={() => allowEdit && setEditor({ open: true, item: l })}>
                    <TableCell className="font-medium">{l.name}{l.is_default && <Badge variant="secondary" className="ml-2">Padrão</Badge>}</TableCell>
                    <TableCell><Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-medium">{stockLocationTypeLabel(l.location_type)}</Badge></TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {[l.street, l.address_number].filter(Boolean).join(', ') || '—'}
                      {l.city && <span> · {l.city}{l.state ? `/${l.state}` : ''}</span>}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider">
                        <StatusDot tone={l.is_active ? 'emerald' : 'muted'} />{l.is_active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {allowEdit && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Ações"><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setEditor({ open: true, item: l })}><Pencil className="mr-2 h-4 w-4" /> Editar</DropdownMenuItem>
                            <DropdownMenuItem disabled={l.is_default && l.is_active} title={l.is_default ? 'O endereço padrão não pode ser inativado' : undefined} onClick={() => toggleActive(l)}><Power className="mr-2 h-4 w-4" /> {l.is_active ? 'Inativar' : 'Ativar'}</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <PaginationFooter totalItems={data?.total ?? 0} currentPage={filters.page} pageSize={filters.pageSize}
          onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
          onPageSizeChange={(pageSize) => setFilters((f) => ({ ...f, pageSize, page: 1 }))}
          pageSizeOptions={[10, 25, 50, 100]} />
      </ListPageContent>
      <StockLocationFormDialog open={editor.open} location={editor.item} onOpenChange={(open) => setEditor((c) => ({ ...c, open }))} />
    </ListPageLayout>
  );
}
